/**
 * @Module: PantryScan
 * @Status: Ready
 * @Summary: Scan your pantry against all recipes — forward/back navigation
 * @Description: Uses all available recipes (saved + samples). Navigate freely
 *   between recipes with prev/next buttons. Each recipe uses PantryCheck.
 *   Summary shows pantry stats and recipe readiness.
 */

import React, { useState } from 'react';
import {
  Box, VStack, HStack, Text, Button, Badge, Progress, IconButton,
} from '@chakra-ui/react';
import { useRecipes } from '../../hooks/useRecipes';
import { getAllRecipes } from '../../hooks/useRecipeById';
import { useKitchen } from '../../contexts/KitchenContext';
import { getRecipeReadiness } from '../../utils/ingredientMatcher';
import { AppIcon } from '../icons/AppIcon';
import PantryCheck from './PantryCheck';

interface PantryScanProps {
  onComplete: () => void;
  onSkip?: () => void;
}

const PantryScan: React.FC<PantryScanProps> = ({ onComplete, onSkip }) => {
  const { recipes: savedRecipes } = useRecipes();
  const allRecipes = getAllRecipes(savedRecipes);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [done, setDone] = useState(false);
  const { profile } = useKitchen();
  const pantry = profile?.pantryStaples || [];

  const totalRecipes = allRecipes.length;
  const progress = totalRecipes > 0 ? ((currentIndex + (done ? 1 : 0)) / totalRecipes) * 100 : 0;

  const handleRecipeComplete = () => {
    if (currentIndex < totalRecipes - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setDone(true);
    }
  };

  const handlePrev = () => setCurrentIndex(prev => Math.max(0, prev - 1));
  const handleNext = () => {
    if (currentIndex < totalRecipes - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setDone(true);
    }
  };

  if (totalRecipes === 0) {
    return (
      <VStack spacing={3} textAlign="center" py={4}>
        <Text color="text.muted" fontSize="sm">No recipes to scan against.</Text>
        <Button variant="kitchen-outline" size="sm" onClick={onComplete}>Done</Button>
      </VStack>
    );
  }

  if (done) {
    const readyCount = allRecipes.filter(r =>
      getRecipeReadiness(r, pantry).fraction >= 0.8
    ).length;

    return (
      <VStack spacing={4} textAlign="center">
        <AppIcon name="ui.checkCircle" size={40} color="var(--chakra-colors-sage-400)" />
        <Text fontSize="lg" fontWeight="bold" fontFamily="heading" color="text.primary">
          Pantry stocked!
        </Text>
        <HStack spacing={3} justify="center">
          <Badge colorScheme="sage" variant="subtle" fontSize="sm" px={3} py={1} borderRadius="full">
            {pantry.length} staples
          </Badge>
          <Badge colorScheme="terracotta" variant="subtle" fontSize="sm" px={3} py={1} borderRadius="full">
            {readyCount}/{totalRecipes} recipes ready
          </Badge>
        </HStack>
        <Button variant="kitchen-primary" w="100%" borderRadius="lg" onClick={onComplete}>
          Done
        </Button>
      </VStack>
    );
  }

  const currentRecipe = allRecipes[currentIndex];

  return (
    <VStack spacing={4} w="100%">
      <Box w="100%">
        <HStack justify="space-between" mb={2}>
          <HStack spacing={2}>
            <IconButton
              aria-label="Previous recipe"
              icon={<AppIcon name="ui.chevronLeft" size={16} />}
              size="sm"
              variant="ghost"
              isDisabled={currentIndex === 0}
              onClick={handlePrev}
            />
            <Text fontSize="xs" fontWeight={600} color="text.muted"
              textTransform="uppercase" letterSpacing="wider">
              {currentIndex + 1} / {totalRecipes}
            </Text>
            <IconButton
              aria-label="Next recipe"
              icon={<AppIcon name="ui.chevronRight" size={16} />}
              size="sm"
              variant="ghost"
              onClick={handleNext}
            />
          </HStack>
          {onSkip && (
            <Button size="xs" variant="ghost" color="text.muted" onClick={onSkip}>
              Done
            </Button>
          )}
        </HStack>
        <Progress value={progress} size="xs" colorScheme="sage" borderRadius="full"
          bg="linen.200" h="4px" />
      </Box>

      <PantryCheck recipe={currentRecipe} onComplete={handleRecipeComplete} />
    </VStack>
  );
};

export default PantryScan;
